import { SetAmlIdentificationRequest } from "@repo/api";
import { AMLData, type AMLFormValues, AMLIncomeSource } from "@repo/logic";

type IncomeInformation = {
    employmentType?: string;
    businessIncomeId?: string;
    otherIncomeId?: string;
};

export const getIncomeSourceInfo = ({ employmentType, businessIncomeId, otherIncomeId }: IncomeInformation) => {
    if (!employmentType || employmentType === AMLIncomeSource.Employment) return;

    if (employmentType !== AMLIncomeSource.Business && employmentType !== AMLIncomeSource.Other) return;

    const incomeSourceId = employmentType === AMLIncomeSource.Business ? businessIncomeId : otherIncomeId;

    if (!incomeSourceId) return;

    const sources = employmentType === AMLIncomeSource.Business ? AMLData.BusinessIncome : AMLData.OtherIncome;

    const IncomeSource = sources.find((incomeSource) => incomeSource.Job === incomeSourceId);

    return IncomeSource;
};

/**
 * Transforms propertySources from form format (Record<number, boolean> with IDs as keys)
 * to API format (Record<string, boolean> with StarpensionName as keys)
 * @param propertySources - Form values with numeric IDs as keys
 * @returns Transformed object with StarpensionName as keys
 */
export const transformPropertySourcesToApiFormat = (
    propertySources: Record<number, boolean>,
): Record<string, boolean> => {
    const result: Record<string, boolean> = {};

    for (const idStr in propertySources) {
        const id = Number(idStr);
        const isChecked = propertySources[id];

        if (isChecked) {
            const propertySource = AMLData.SourceOfProperty.find((source) => source.Id === id);
            if (propertySource) {
                result[propertySource.StarpensionName] = true;
            }
        }
    }

    return result;
};

export const mapAMLRequest = (values: AMLFormValues, token?: string): SetAmlIdentificationRequest => {
    const {
        employmentType,
        businessIncomeId,
        otherIncomeId,
        incomeSourceSpecification,
        politicallyExposed: pep,
        propertySources,
        propertySourceSpecification,
        taxNumber: taxNumberFieldValue,
        taxResidence: domicil,
        greenCardHolder: greenCard,
        averageIncome: income,
        usaResident: usTaxResident,
    } = values;

    const sourceOfIncome = (() => {
        if (employmentType === AMLIncomeSource.Business || employmentType === AMLIncomeSource.Employment)
            return employmentType;
        return otherIncomeId;
    })();

    const companySourceOfIncome = (() => {
        if (employmentType === AMLIncomeSource.Business) {
            return businessIncomeId;
        }

        if (employmentType === AMLIncomeSource.Other && otherIncomeId === "Jiné") {
            return incomeSourceSpecification;
        }
    })();

    const sourceOfProperty = (() => {
        const sources: string[] = [];

        const other = AMLData.SourceOfProperty.find((item) => item.StarpensionName === "Jiné")!;

        for (const [source, selected] of Object.entries(propertySources)) {
            const sourceId = Number(source);
            const starPensionName = AMLData.SourceOfProperty.find((item) => item.Id === sourceId)!.StarpensionName;
            if (selected && sourceId !== other.Id) {
                sources.push(starPensionName);
            }
        }

        const sortedSources = [...sources];
        sortedSources.sort((a, b) => a.localeCompare(b));

        if (propertySources[other.Id] && propertySourceSpecification)
            sortedSources.push(`${other.StarpensionName}:${propertySourceSpecification}`);

        return sortedSources.join("|");
    })();

    const taxNumber = domicil !== "CZE" ? taxNumberFieldValue : undefined;

    return {
        sourceOfIncome,
        companySourceOfIncome,
        sourceOfProperty,
        domicil,
        greenCard,
        pep,
        income,
        taxNumber,
        token,
        usTaxResident,
    };
};
